import { Alert, Divider, Stack, Typography } from "@mui/material";
import { Flex, Loader } from "@zfp-akademie-a-s/zfp-design";
import { CheckIcon, CloseIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { PortfolioEvaluationItemEvaluationType } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationItemEvaluationType";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { InvestmentsNsureRiskProfile } from "./InvestmentsNsureRiskProfile";
import { type NsureSelection, NsureSelectionContext } from "./InvestmentsNsureSelectionContext";
import type { NsureEvaluation } from "./useNsureEvaluation";
import { getRiskToleranceWarningItem } from "./utils";

// whether InvestmentsNsureEvaluation shows anything (the criteria, or a loader on the way to them),
// so the card adds it, and the divider above it, only then
export const hasEvaluationContent = (selection: NsureSelection, evaluation: NsureEvaluation) =>
  evaluation.isQuestionnaireLoading ||
  (!evaluation.isQuestionnaireNotFound &&
    !!selection &&
    !evaluation.isError &&
    (evaluation.isPending || evaluation.isDataStale || !!evaluation.evaluationPlan));

// The risk profile and the target market criteria, the last blocks of the selection card. What the
// evaluation comes to is InvestmentsNsureEvaluationAlert's, under the card.
export const InvestmentsNsureEvaluation = () => {
  const { t } = useResource();
  const { selection, evaluation } = useContext(NsureSelectionContext);
  const { evaluationPlan, items, isPending, isDataStale, isOk } = evaluation;

  if (!hasEvaluationContent(selection, evaluation)) return null;

  if (evaluation.isQuestionnaireLoading || isPending || isDataStale || !evaluationPlan) return <Loader />;

  const riskToleranceItem = getRiskToleranceWarningItem(evaluationPlan);
  const evaluationLabels: Record<string, string> = {
    InvestorType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.InvestorType),
    CustomerCategory: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.CustomerCategory),
    RiskToleranceType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.RiskToleranceType),
    LossCapabilityType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.LossCapabilityType),
    InvestmentPreferenceType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.InvestmentPreferenceType),
    ESGType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EsgType),
    InvestmentHorizon: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.InvestmentHorizon),
  };

  const renderItem = (type: PortfolioEvaluationItemEvaluationType) => (
    <Flex key={type} alignItems="center" gap={Spacing.M} minHeight={30}>
      {isOk(type) ? (
        <CheckIcon sx={{ color: "success.dark", fontSize: 18, flexShrink: 0 }} />
      ) : (
        <CloseIcon sx={{ color: "error.main", fontSize: 18, flexShrink: 0 }} />
      )}
      <Typography variant="body2" flex={1}>
        {evaluationLabels[type] ?? type}
      </Typography>
      {!isOk(type) && (
        <Typography variant="caption" color="error.main">
          {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.NotMet)}
        </Typography>
      )}
    </Flex>
  );

  return (
    <Stack spacing={Spacing.L} divider={<Divider />}>
      {riskToleranceItem && <InvestmentsNsureRiskProfile riskItem={riskToleranceItem} />}
      {items.length > 0 && (
        <Stack spacing={Spacing.S}>
          <Typography variant="body2" fontWeight={700}>
            {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.Title)}
          </Typography>
          <Stack>{items.map(renderItem)}</Stack>
        </Stack>
      )}
    </Stack>
  );
};

// What the evaluation comes to, under the selection card: a missing questionnaire, an error, or
// whether the investment can be arranged, naming the criteria it fails.
export const InvestmentsNsureEvaluationAlert = () => {
  const { t } = useResource();
  const { selection, evaluation } = useContext(NsureSelectionContext);
  const { evaluationPlan, items, hasWarning, isPending, isError, isDataStale, isOk } = evaluation;

  if (evaluation.isQuestionnaireLoading) return null;

  if (evaluation.isQuestionnaireNotFound) {
    return <Alert severity="warning">{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EvaluationNotFound)}</Alert>;
  }

  if (!selection || isPending || isDataStale) return null;

  if (isError) return <Alert severity="error">{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.EvaluateError)}</Alert>;

  if (!evaluationPlan) return null;

  if (!hasWarning) {
    return <Alert severity="success">{t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.SuccessTitle)}</Alert>;
  }

  const unmetLabels: Record<string, string> = {
    InvestorType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.InvestorType),
    CustomerCategory: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.CustomerCategory),
    RiskToleranceType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.RiskToleranceType),
    LossCapabilityType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.LossCapabilityType),
    InvestmentPreferenceType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.InvestmentPreferenceType),
    ESGType: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.ESGType),
    InvestmentHorizon: t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.UnmetCriteria.InvestmentHorizon),
  };
  // without a criterion to name, the alert keeps the bare sentence
  const unmetCriteria = items.filter((type) => !isOk(type) && unmetLabels[type]).map((type) => unmetLabels[type]);

  return (
    <Alert severity="error">
      {unmetCriteria.length > 0
        ? t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.WarningTitleWithReason, {
            criteria: new Intl.ListFormat("cs-CZ", { type: "conjunction" }).format(unmetCriteria),
          })
        : t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.WarningTitle)}
    </Alert>
  );
};
